import React, { useState, useEffect, useRef } from 'react';
import { Field, Textarea, Button, DateTimePicker, parseNaive, pad2 } from './UI';

const validate = (values) => {
  const errors = {};
  if (values.description && values.description.length > 2000)
    errors.description = 'Не более 2000 символов';
  if (values.totalSlots !== '' && values.totalSlots !== null && values.totalSlots !== undefined) {
    const n = Number(values.totalSlots);
    if (!Number.isInteger(n) || n < 0)
      errors.totalSlots = 'Укажите целое число от 0';
  }
  return errors;
};


// Добавляем минуты к наивной дате — возвращаем строку "YYYY-MM-DDTHH:mm:ss"
function addMinutesNaive(naiveStr, minutes) {
  const p = parseNaive(naiveStr);
  if (!p) return null;
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute));
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:00`;
}

// Варианты длительности
const DURATION_OPTIONS = [
  { label: '30 мин', minutes: 30 },
  { label: '1 час', minutes: 60 },
  { label: '1.5 ч', minutes: 90 },
  { label: '2 часа', minutes: 120 },
  { label: '2.5 ч', minutes: 150 },
  { label: '3 часа', minutes: 180 },
];

/**
 * EventForm — форма создания / редактирования события.
 * onValuesChange(values) — вызывается при каждом изменении значений
 *
 * availableChats      — список чатов для выбора места публикации
 *   [{telegramChatId, title, chatType}], пустой массив = скрыть селектор
 * selectedChatId      — текущий выбранный chatId (null = личный чат)
 * onChatChange        — callback(chatId | null) при смене выбора
 *
 * isInlineMode        — пользователь открыл форму через inline-вызов бота (@bot в чате)
 * inlineModeOverridden — пользователь нажал "выбрать чат": показываем обычный селектор
 * onOverrideInlineMode — callback: переключиться на ручной выбор чата
 * onRestoreInlineMode  — callback: вернуться к inline-публикации
 *
 * onSubmitSaveOnly    — если передан и не inline-режим: показываем два разных действия.
 *                       onSubmitSaveOnly(values) = «Сохранить» (без публикации)
 *                       onSubmit(values)          = «Сохранить и опубликовать»
 * loadingSaveOnly     — состояние загрузки кнопки «Сохранить»
 * saveOnlyLabel       — подпись кнопки «Сохранить» (по умолчанию — «Сохранить»)
 */
const EventForm = ({
  initialValues = {},
  onSubmit,
  submitLabel = 'Сохранить',
  loading = false,
  chatsLoading = false,
  onCancel,
  onValuesChange,
  availableChats = [],
  selectedChatId = null,
  onChatChange,
  isInlineMode = false,
  inlineModeOverridden = false,
  onOverrideInlineMode,
  onRestoreInlineMode,
  onSubmitSaveOnly,
  loadingSaveOnly = false,
  saveOnlyLabel = 'Сохранить',
}) => {
  const [values, setValues] = useState({
    description: initialValues.description || '',
    eventDate: initialValues.eventDate || null,
    eventEndDate: initialValues.eventEndDate || null,
    totalSlots: initialValues.totalSlots != null ? String(initialValues.totalSlots) : '',
    // ?? вместо !== undefined: null (событие без слотов) тоже даёт дефолт true,
    // чтобы при добавлении слотов в режиме редактирования лист ожидания включался автоматически
    waitingListEnabled: initialValues.waitingListEnabled ?? true,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
    if (touched[field]) {
      const errs = validate({ ...values, [field]: value });
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const blur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    const errs = validate(values);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const setField = (field, val) => {
    setValues((v) => ({ ...v, [field]: val }));
  };

  // Подготовить данные формы: провалидировать и собрать объект для отправки.
  // Возвращает null если есть ошибки (они уже выставлены в state).
  const prepareSubmitValues = () => {
    const allTouched = Object.fromEntries(Object.keys(values).map((k) => [k, true]));
    setTouched(allTouched);
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return null;

    return {
      description: values.description.trim() || null,
      eventDate: values.eventDate || null,
      eventEndDate: values.eventEndDate || null,
      totalSlots: values.totalSlots !== '' && values.totalSlots !== null ? Number(values.totalSlots) : null,
      waitingListEnabled: values.totalSlots !== '' && values.totalSlots !== null ? values.waitingListEnabled : null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prepared = prepareSubmitValues();
    if (!prepared) return;
    await onSubmit(prepared);
  };

  // Обработчик кнопки «Сохранить» (только запись в БД, без публикации)
  const handleSaveOnly = async () => {
    const prepared = prepareSubmitValues();
    if (!prepared) return;
    await onSubmitSaveOnly(prepared);
  };

  // Ширина строки пикеров — для передачи в dropdown
  const rowRef = useRef(null);
  const [rowWidth, setRowWidth] = useState(null);
  useEffect(() => {
    if (!rowRef.current) return;
    const ro = new ResizeObserver(() => {
      setRowWidth(rowRef.current?.offsetWidth);
    });
    ro.observe(rowRef.current);
    setRowWidth(rowRef.current.offsetWidth);
    return () => ro.disconnect();
  }, []);

  return (
    <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Даты начала и окончания — одна строка ── */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, letterSpacing: 0.2 }}>
          Дата и время
        </div>

        {/* Строка с двумя пикерами */}
        <div ref={rowRef} style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
          {/* Пикер начала */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DateTimePicker
              value={values.eventDate}
              onChange={(val) => {
                setField('eventDate', val);
                if (values.eventEndDate) setField('eventEndDate', null);
              }}
              error={errors.eventDate}
              placeholder="Без времени начала"
              style={{ borderRadius: '10px 0 0 10px', borderRight: 'none' }}
              dropdownWidth={rowWidth ? `${rowWidth}px` : '200%'}
            />
          </div>

          {/* Разделитель */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
            background: '#f3f4f6',
            border: '1.5px solid #e5e7eb',
            borderLeft: 'none',
            borderRight: 'none',
            zIndex: 1,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </div>

          {/* Пикер окончания */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <DateTimePicker
              value={values.eventEndDate}
              onChange={(val) => {
                setField('eventEndDate', val);
              }}
              error={errors.eventEndDate}
              placeholder="Без времени окончания"
              style={{ borderRadius: '0 10px 10px 0', borderLeft: 'none' }}
              dropdownWidth={rowWidth ? `${rowWidth}px` : '200%'}
              dropdownRight
              defaultDate={values.eventDate ? (() => { const p = parseNaive(values.eventDate); return p ? { year: p.year, month: p.month, day: p.day } : null; })() : null}
              skipCalendar={!!values.eventDate}
              headerSlot={values.eventDate ? (close) => (
                <div style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, display: 'block', marginBottom: 7 }}>
                    ⚡ Быстрый выбор длительности
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {DURATION_OPTIONS.map(({ label, minutes }) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() => {
                          setField('eventEndDate', addMinutesNaive(values.eventDate, minutes));
                          close();
                        }}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 16,
                          border: '1.5px solid #c7d2fe',
                          background: '#f5f3ff',
                          color: '#4f46e5',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.12s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#ede9fe'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f5f3ff'; }}
                      >
                        + {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            />
          </div>
        </div>
        {/* Ошибки */}
        {(errors.eventDate || errors.eventEndDate) && (
          <div style={{ marginTop: 4 }}>
            {errors.eventDate && (
              <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                Начало: {errors.eventDate}
              </span>
            )}
            {errors.eventEndDate && (
              <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                Окончание: {errors.eventEndDate}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Ограничение мест ── */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, letterSpacing: 0.2 }}>
          Количество мест
        </div>

        {/* Input row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Number input */}
          <div style={{ position: 'relative', flex: 1 }}>
            <div style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={values.totalSlots ? '#6366f1' : '#9ca3af'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={values.totalSlots}
              onChange={e => {
                // Разрешаем только цифры
                const val = e.target.value.replace(/[^0-9]/g, '');
                setValues(v => ({ ...v, totalSlots: val }));
                if (touched.totalSlots) {
                  const errs = validate({ ...values, totalSlots: val });
                  setErrors(prev => ({ ...prev, totalSlots: errs.totalSlots }));
                }
              }}
              onBlur={() => {
                setTouched(t => ({ ...t, totalSlots: true }));
                const errs = validate(values);
                setErrors(prev => ({ ...prev, totalSlots: errs.totalSlots }));
              }}
              placeholder="Без ограничений"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 10px 9px 32px',
                borderRadius: 10,
                border: `1.5px solid ${errors.totalSlots ? '#fca5a5' : values.totalSlots ? '#a5b4fc' : '#e5e7eb'}`,
                fontSize: 14, color: '#111827',
                background: values.totalSlots ? '#fafafe' : '#fff',
                outline: 'none',
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onFocus={e => { e.target.style.borderColor = '#6366f1'; }}
              onBlurCapture={e => { if (!errors.totalSlots) e.target.style.borderColor = values.totalSlots ? '#a5b4fc' : '#e5e7eb'; }}
            />
          </div>

          {/* Clear button */}
          {values.totalSlots && (
            <button
              type="button"
              onClick={() => setValues(v => ({ ...v, totalSlots: '', waitingListEnabled: true }))}
              title="Снять ограничение"
              style={{
                padding: '8px 10px', borderRadius: 9,
                border: '1.5px solid #e5e7eb', background: '#f9fafb',
                color: '#9ca3af', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#dc2626'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#9ca3af'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* Error */}
        {errors.totalSlots && (
          <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            {errors.totalSlots}
          </span>
        )}

        {/* Waiting list toggle — появляется только когда указан лимит */}
        {values.totalSlots && !errors.totalSlots && (
          <div style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: values.waitingListEnabled ? '#f0fdf4' : '#fafafe',
            border: `1.5px solid ${values.waitingListEnabled ? '#86efac' : '#e0e7ff'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            transition: 'all 0.2s',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: values.waitingListEnabled ? '#15803d' : '#4f46e5', marginBottom: 2 }}>
                {values.waitingListEnabled ? '📋 Лист ожидания включён' : `✋ Максимум ${values.totalSlots} участников`}
              </div>
              <div style={{ fontSize: 11, color: values.waitingListEnabled ? '#16a34a' : '#6366f1', lineHeight: 1.4 }}>
                {values.waitingListEnabled
                  ? `После ${values.totalSlots} мест новые участники попадают в очередь`
                  : `Свыше лимита запись невозможна`}
              </div>
            </div>

            {/* Toggle switch */}
            <button
              type="button"
              onClick={() => setValues(v => ({ ...v, waitingListEnabled: !v.waitingListEnabled }))}
              style={{
                position: 'relative',
                width: 44, height: 24, borderRadius: 12,
                border: 'none', cursor: 'pointer', flexShrink: 0,
                background: values.waitingListEnabled
                  ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                  : 'linear-gradient(135deg, #a5b4fc, #6366f1)',
                transition: 'background 0.2s',
                padding: 0,
              }}
            >
              <div style={{
                position: 'absolute',
                top: 3, left: values.waitingListEnabled ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
        )}

      </div>

      {/* ── Описание ── */}
      <Field label="Описание" error={errors.description}>
        <Textarea
          value={values.description}
          onChange={set('description')}
          onBlur={blur('description')}
          placeholder="Информация о событии..."
          maxLength={2000}
          error={errors.description}
          rows={4}
        />
      </Field>

      {/* ── Куда публиковать ── */}
      {/* Inline-режим: бот упомянут в чате, публикация вернётся туда автоматически */}
      {isInlineMode && !inlineModeOverridden && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, letterSpacing: 0.2 }}>
            Куда опубликовать
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 10px 9px 32px',
            borderRadius: 10,
            border: '1.5px solid #a5b4fc',
            background: '#fafafe',
            position: 'relative',
          }}>
            {/* Иконка чата слева — как у select */}
            <div style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>

            {/* Текст — выбранное значение */}
            <span style={{ flex: 1, fontSize: 14, color: '#111827' }}>
              Поделиться в текущем чате
            </span>

            {/* Иконка-кнопка смены */}
            {onOverrideInlineMode && (
              <button
                type="button"
                onClick={onOverrideInlineMode}
                title="Выбрать другой чат"
                style={{
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26,
                  borderRadius: 7,
                  border: '1.5px solid #e0e7ff',
                  background: '#fff',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.borderColor = '#a5b4fc'; e.currentTarget.style.color = '#6366f1'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e0e7ff'; e.currentTarget.style.color = '#9ca3af'; }}
              >
                {/* Иконка "сменить" — стрелки по кругу */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="17 1 21 5 17 9"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 23 3 19 7 15"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Обычный селектор чата — в non-inline режиме или когда пользователь переключился из inline */}
      {(onChatChange && (!isInlineMode || inlineModeOverridden)) && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8, letterSpacing: 0.2 }}>
            Куда опубликовать
          </div>
          <div style={{ position: 'relative' }}>
            {/* Иконка слева */}
            <div style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <select
              value={chatsLoading ? '' : (selectedChatId ?? '')}
              onChange={e => onChatChange?.(e.target.value ? Number(e.target.value) : null)}
              disabled={chatsLoading}
              style={{
                width: '100%',
                padding: '9px 10px 9px 32px',
                borderRadius: 10,
                border: `1.5px solid ${chatsLoading ? '#e5e7eb' : '#a5b4fc'}`,
                fontSize: 14,
                color: chatsLoading ? '#9ca3af' : '#111827',
                background: chatsLoading ? '#f3f4f6' : '#fafafe',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                cursor: chatsLoading ? 'wait' : 'pointer',
                transition: 'border-color 0.15s, background 0.15s, color 0.15s',
              }}
              onFocus={e => { if (!chatsLoading) e.target.style.borderColor = '#6366f1'; }}
              onBlur={e => { if (!chatsLoading) e.target.style.borderColor = '#a5b4fc'; }}
            >
              {chatsLoading ? (
                <option value="">Загрузка чатов...</option>
              ) : (
                <>
                  <option value="">Личный чат с ботом</option>
                  {availableChats.length === 0 && (
                    <option value="" disabled style={{ color: '#9ca3af' }}>
                      — Добавьте бота в группу, чтобы публиковать туда
                    </option>
                  )}
                  {availableChats.map(chat => (
                    <option key={chat.telegramChatId} value={chat.telegramChatId}>
                      {chat.title || `Чат ${chat.telegramChatId}`}
                    </option>
                  ))}
                </>
              )}
            </select>
            {/* Стрелка вниз */}
            <div style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
          {/* Кнопка возврата к inline-режиму */}
          {isInlineMode && inlineModeOverridden && onRestoreInlineMode && (
            <button
              type="button"
              onClick={onRestoreInlineMode}
              style={{
                marginTop: 8,
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none',
                color: '#6366f1', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', padding: '2px 0',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#4f46e5'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#6366f1'; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Вернуться к «Поделиться в текущем чате»
            </button>
          )}
        </div>
      )}

      {/* ── Кнопки действий ── */}
      {onSubmitSaveOnly && !isInlineMode ? (
        // Два действия: «Сохранить и опубликовать» и «Сохранить» (без публикации)
        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
          <Button
            type="submit"
            loading={loading}
            disabled={loadingSaveOnly || chatsLoading}
            style={{ flex: 2 }}
          >
            {chatsLoading ? 'Загрузка чатов...' : submitLabel}
          </Button>
          <Button
            type="button"
            variant="secondary"
            loading={loadingSaveOnly}
            disabled={loading || chatsLoading}
            onClick={handleSaveOnly}
            style={{ flex: 1 }}
          >
            {chatsLoading ? 'Загрузка...' : saveOnlyLabel}
          </Button>
        </div>
      ) : (
        // Одно действие: либо обычный submit, либо с кнопкой Отмена (режим редактирования)
        <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel} style={{ flex: 1 }}>
              Отмена
            </Button>
          )}
          <Button type="submit" loading={loading} disabled={chatsLoading} style={{ flex: 2 }}>
            {chatsLoading ? 'Загрузка чатов...' : submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
};

export default EventForm;